/**
 * useGoogleAds Composable
 * Manages Google AdSense/DFP ad loading with proper error handling
 * Supports multiple review types, layouts, and ad configurations
 */

import { ref, nextTick, inject } from 'vue'
import googleAdsConfig from '@/config/googleAds.config.js'

// Global State (Singleton) - prevents re-initialization on every composable call
const gptInitialized = ref(false)
const gptLoadFailed = ref(false)
const gptLoadTimeout = ref(false)
const adBlockerDetected = ref(false)
const adLoadErrors = ref({})
const definedSlots = new Set()

export function useGoogleAds() {
  // Inject noAdsPage flag
  const noAdsPage = inject('noAdsPage', false)

  // Helper functions - defined before use

  /**
   * Add fallback banners to ad containers that don't have them
   * Called during initialization to handle containers injected via backend HTML
   */
  const addFallbackBannersToContainers = () => {
    // Find all inner-banner containers (injected from backend)
    const innerBanners = document.querySelectorAll('.inner-banner:not(:has(.fallback-banner-link))')

    innerBanners.forEach(container => {
      // Create fallback banners for this container
      const desktopLink = document.createElement('a')
      desktopLink.href = 'https://www.reviewstream.com/'
      desktopLink.target = '_blank'
      desktopLink.rel = 'nofollow sponsored'
      desktopLink.className = 'fallback-banner-link fallback-desktop'
      desktopLink.style.display = 'none'

      const desktopImg = document.createElement('img')
      desktopImg.src = 'https://reviewstream.com/banners/banner_970x250.png'
      desktopImg.alt = 'Advertisement'
      desktopImg.className = 'fallback-banner'

      desktopLink.appendChild(desktopImg)
      container.appendChild(desktopLink)

      const mobileLink = document.createElement('a')
      mobileLink.href = 'https://www.reviewstream.com/'
      mobileLink.target = '_blank'
      mobileLink.rel = 'nofollow sponsored'
      mobileLink.className = 'fallback-banner-link fallback-mobile'
      mobileLink.style.display = 'none'

      const mobileImg = document.createElement('img')
      mobileImg.src = 'https://reviewstream.com/banners/banner_360x360.png'
      mobileImg.alt = 'Advertisement'
      mobileImg.className = 'fallback-banner'

      mobileLink.appendChild(mobileImg)
      container.appendChild(mobileLink)

      // console.log(`[Google Ads] Added fallback banners to container: ${container.id}`)
    })

    // console.log(`[Google Ads] Added fallback banners to ${innerBanners.length} inner-banner containers`)
  }

  /**
   * Show fallback banner when Google ad fails
   */
  const showFallbackBanner = (slotId, container) => {
    // Find pre-existing fallback banners in the container
    let desktopFallback = container.querySelector('.fallback-desktop')
    let mobileFallback = container.querySelector('.fallback-mobile')

    // If fallback banners don't exist, create them
    if (!desktopFallback && !mobileFallback) {
      // console.log(`[Google Ads] Creating fallback banners for container ${slotId}`)

      const desktopLink = document.createElement('a')
      desktopLink.href = 'https://www.reviewstream.com/'
      desktopLink.target = '_blank'
      desktopLink.rel = 'nofollow sponsored'
      desktopLink.className = 'fallback-banner-link fallback-desktop'

      const desktopImg = document.createElement('img')
      desktopImg.src = 'https://reviewstream.com/banners/banner_970x250.png'
      desktopImg.alt = 'Advertisement'
      desktopImg.className = 'fallback-banner'

      desktopLink.appendChild(desktopImg)
      container.appendChild(desktopLink)
      desktopFallback = desktopLink

      const mobileLink = document.createElement('a')
      mobileLink.href = 'https://www.reviewstream.com/'
      mobileLink.target = '_blank'
      mobileLink.rel = 'nofollow sponsored'
      mobileLink.className = 'fallback-banner-link fallback-mobile'

      const mobileImg = document.createElement('img')
      mobileImg.src = 'https://reviewstream.com/banners/banner_360x360.png'
      mobileImg.alt = 'Advertisement'
      mobileImg.className = 'fallback-banner'

      mobileLink.appendChild(mobileImg)
      container.appendChild(mobileLink)
      mobileFallback = mobileLink
    }

    // Remove any inline display styles that might be hiding the fallbacks
    if (desktopFallback) desktopFallback.style.removeProperty('display')
    if (mobileFallback) mobileFallback.style.removeProperty('display')

    // Detect if this is a mobile container
    const isMobile = container.classList.contains('top-banner-mobile') ||
                     container.classList.contains('mobile-ad') ||
                     window.innerWidth < 768

    // Hide both fallbacks first using style
    if (desktopFallback) desktopFallback.style.display = 'none'
    if (mobileFallback) mobileFallback.style.display = 'none'

    // Show the appropriate fallback banner
    if (isMobile && mobileFallback) {
      mobileFallback.style.display = 'block'
    } else if (!isMobile && desktopFallback) {
      desktopFallback.style.display = 'block'
    } else if (desktopFallback) {
      // Default to desktop if no mobile fallback
      desktopFallback.style.display = 'block'
    }

    // CRITICAL: Remove ad-failed class first, then add ad-fallback
    // The order matters because of CSS specificity
    container.classList.remove('ad-failed')
    container.classList.remove('ad-loaded')

    // Add ad-fallback class AFTER removing ad-failed
    container.classList.add('ad-fallback')

    // Set data-ad-status to 'fallback' (not 'failed')
    container.setAttribute('data-ad-status', 'fallback')

    // Force display inline style to override CSS
    container.style.display = 'flex'
    container.style.height = 'auto'
    container.style.minHeight = isMobile ? '360px' : '50px'
    container.style.maxHeight = 'none'
    container.style.opacity = '1'
    container.style.pointerEvents = 'auto'
    container.style.margin = '10px 0'

    // console.log(`[Google Ads] Slot ${slotId} - fallback banner shown (${isMobile ? 'mobile' : 'desktop'})`)
  }

  /**
   * Check if Google's iframe ad is actually empty (has no visible ads inside)
   */
  const isIframeAdActuallyEmpty = (container) => {
    try {
      // Find the iframe in the container
      const iframe = container.querySelector('iframe[id^="google_ads_iframe_"]')
      if (!iframe) {
        // console.log('[Google Ads] No iframe found in container')
        return true // No iframe = empty
      }

      // Try to access iframe content
      let iframeDoc
      try {
        iframeDoc = iframe.contentDocument || iframe.contentWindow.document
      } catch (e) {
        // Cross-origin iframe - can't access content
        // console.log('[Google Ads] Cannot access iframe content (cross-origin)')
        return false // Assume not empty if we can't check
      }

      if (!iframeDoc) {
        // console.log('[Google Ads] No iframe document found')
        return true
      }

      // Look for all <ins class="adsbygoogle"> elements inside the iframe
      const adsbygoogleElements = iframeDoc.querySelectorAll('ins.adsbygoogle')

      if (adsbygoogleElements.length === 0) {
        // console.log('[Google Ads] No adsbygoogle elements found in iframe')
        return true // No ads found = empty
      }

      // Check if ALL adsbygoogle elements are hidden or have no size
      let allHidden = true
      adsbygoogleElements.forEach((adElement, index) => {
        const style = window.getComputedStyle(adElement)
        const isVisible = style.display !== 'none' &&
                         style.visibility !== 'hidden' &&
                         style.opacity !== '0' &&
                         adElement.offsetWidth > 0 &&
                         adElement.offsetHeight > 0

        // console.log(`[Google Ads] Ad element ${index}: display=${style.display}, visibility=${style.visibility}, opacity=${style.opacity}, offsetWidth=${adElement.offsetWidth}, offsetHeight=${adElement.offsetHeight}, visible=${isVisible}`)

        if (isVisible) {
          allHidden = false
        }
      })

      // console.log(`[Google Ads] All ads hidden: ${allHidden}`)
      return allHidden

    } catch (error) {
      // console.warn('[Google Ads] Error checking iframe content:', error)
      return false // On error, assume not empty to be safe
    }
  }

  // Add fallback banners to containers on initialization
  // This handles inner-banner containers injected via backend HTML
  if (typeof window !== 'undefined') {
    // Wait for DOM to be ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', addFallbackBannersToContainers)
    } else {
      // DOM is already ready
      addFallbackBannersToContainers()
    }
  }

  /**
   * Detect ad blockers
   */
  const detectAdBlocker = () => {
    const adTest = document.createElement('div')
    adTest.innerHTML = '&nbsp;'
    adTest.className = 'adsbox'
    adTest.style.position = 'absolute'
    adTest.style.left = '-10000px'
    document.body.appendChild(adTest)

    setTimeout(() => {
      if (adTest.offsetHeight === 0) {
        adBlockerDetected.value = true
        // console.warn('[Google Ads] Ad blocker detected')
        markAllAdContainersAsFailed()
      }
      document.body.removeChild(adTest)
    }, 100)
  }

  /**
   * Initialize Google Publisher Tag (GPT)
   */
  const initializeGPT = () => {
    if (gptInitialized.value || gptLoadFailed.value || noAdsPage) return

    // console.log('[Google Ads] Initializing GPT...')

    // Set timeout for GPT loading
    const timeoutId = setTimeout(() => {
      // console.warn('[Google Ads] GPT loading timeout')
      gptLoadTimeout.value = true
      markAllAdContainersAsFailed()
    }, googleAdsConfig.gptSettings.loadTimeout)

    // Create GPT script element
    const gptScript = document.createElement('script')
    gptScript.async = true
    gptScript.src = googleAdsConfig.gptSettings.scriptUrl

    gptScript.onload = () => {
      clearTimeout(timeoutId)
      window.googletag = window.googletag || { cmd: [] }
      window.googletag.cmd.push(function() {
        try {
          // Destroy any existing slots (ghosts/legacy)
          window.googletag.destroySlots()
          // console.log('[Google Ads] Destroyed existing/legacy slots')

          window.googletag.pubads().enableAsyncRendering()
          window.googletag.pubads().collapseEmptyDivs(true)
          window.googletag.pubads().enableSingleRequest()

          // Add slot render ended event listener
          window.googletag.pubads().addEventListener('slotRenderEnded', function(event) {
            const slotId = event.slot.getSlotElementId()
            const container = document.getElementById(slotId)

            if (!container) return

            if (event.isEmpty) {
              // Slot rendered but no ad was returned - show fallback banner
              container.classList.add('ad-failed')
              container.classList.remove('ad-loaded')
              container.setAttribute('data-ad-status', 'failed')
              container.setAttribute('data-ad-reason', 'empty')
              // console.log(`[Google Ads] Slot ${slotId} rendered empty - showing fallback banner`)
              // Show fallback banner immediately
              showFallbackBanner(slotId, container)
            } else if (event.size && event.size[0] === 1 && event.size[1] === 1) {
              // Slot rendered as 1x1 pixel (likely filtered out-of-page format)
              container.classList.add('ad-failed')
              container.classList.remove('ad-loaded')
              container.setAttribute('data-ad-status', 'failed')
              container.setAttribute('data-ad-reason', '1x1-pixel')
              // console.log(`[Google Ads] Slot ${slotId} returned 1x1 pixel - treating as empty`)
              showFallbackBanner(slotId, container)
            } else {
              // Google reports ad as loaded, but we need to verify it's not blank
              // Wait a moment for iframe to load, then check for actual content
              setTimeout(() => {
                if (isIframeAdActuallyEmpty(container)) {
                  // Iframe is loaded but actually empty - show fallback
                  container.classList.add('ad-failed')
                  container.classList.remove('ad-loaded')
                  container.setAttribute('data-ad-status', 'failed')
                  container.setAttribute('data-ad-reason', 'iframe-empty')
                  // console.log(`[Google Ads] Slot ${slotId} iframe is empty - showing fallback banner`)
                  showFallbackBanner(slotId, container)
                } else {
                  // Ad was successfully rendered with real content
                  container.classList.add('ad-loaded')
                  container.classList.remove('ad-failed', 'ad-fallback')
                  container.setAttribute('data-ad-status', 'loaded')
                  container.setAttribute('data-ad-size', event.size.join('x'))
                  // console.log(`[Google Ads] Slot ${slotId} loaded successfully (${event.size.join('x')})`)
                }
              }, 500) // Wait 500ms for iframe content to load
            }
          })

          window.googletag.enableServices()
          gptInitialized.value = true
          // console.log('[Google Ads] GPT initialized successfully')
        } catch (error) {
          // console.error('[Google Ads] GPT initialization error:', error)
          gptLoadFailed.value = true
        }
      })
    }

    gptScript.onerror = () => {
      clearTimeout(timeoutId)
      // console.error('[Google Ads] Failed to load GPT script')
      gptLoadFailed.value = true
      markAllAdContainersAsFailed()
    }

    document.head.appendChild(gptScript)
  }

  /**
   * Mark all ad containers as failed and show fallback banners
   */
  const markAllAdContainersAsFailed = () => {
    const containers = document.querySelectorAll('.review-top-banner, .inner-banner, .comment-banner')
    // console.log(`[Google Ads] Marking ${containers.length} containers as failed and showing fallbacks`)
    containers.forEach(container => {
      if (container) {
        showFallbackBanner(container.id || 'unknown', container)
      }
    })
  }

  /**
   * Show fallback banners for all ad containers immediately
   * Call this when Google ads are not configured or unavailable
   */
  const showAllFallbackBanners = () => {
    const containers = document.querySelectorAll('.review-top-banner, .inner-banner, .comment-banner')
    // console.log(`[Google Ads] Showing fallback banners for ${containers.length} containers`)
    containers.forEach(container => {
      if (container) {
        showFallbackBanner(container.id || 'unknown', container)
      }
    })
  }

  /**
   * Mark individual container as failed and show fallback banner
   */
  const markAdContainerFailed = (slotId, reason) => {
    const container = document.getElementById(slotId)
    if (container) {
      container.setAttribute('data-ad-error', reason)
      // console.warn(`[Google Ads] Container ${slotId} failed: ${reason}, showing fallback`)
      // Show fallback banner instead of hiding container
      showFallbackBanner(slotId, container)
    }
    adLoadErrors.value[slotId] = reason
  }

  /**
   * Create size mapping
   */
  const createSizeMapping = (mappingType) => {
    if (!window.googletag || !window.googletag.sizeMapping) {
      return null
    }

    const mappingConfig = googleAdsConfig.sizeMappings[mappingType]
    if (!mappingConfig) {
      // console.warn(`[Google Ads] Unknown mapping type: ${mappingType}`)
      return null
    }

    let mapping = window.googletag.sizeMapping()
    mappingConfig.forEach(config => {
      mapping = mapping.addSize(config.viewport, config.sizes)
    })
    return mapping.build()
  }

  /**
   * Build full ad unit path
   */
  const buildAdUnitPath = (adUnitName) => {
    return `/${googleAdsConfig.networkCode}/${adUnitName}`
  }

  /**
   * Define ad slot
   */
  const defineAdSlot = (slotId, adUnitPath, sizes, mapping = null) => {
    // Prevent duplicate definitions
    if (definedSlots.has(slotId)) {
      // console.warn(`[Google Ads] Slot ${slotId} already defined, skipping`)
      return true
    }

    if (adBlockerDetected.value) {
      markAdContainerFailed(slotId, 'Ad blocker detected')
      return false
    }

    if (!window.googletag || !window.googletag.cmd) {
      markAdContainerFailed(slotId, 'GPT not initialized')
      return false
    }

    try {
      window.googletag.cmd.push(function() {
        try {
          const slot = window.googletag.defineSlot(adUnitPath, sizes, slotId)
          if (!slot) {
            markAdContainerFailed(slotId, 'Failed to define ad slot')
            return
          }
          if (mapping) {
            slot.defineSizeMapping(mapping)
          }
          slot.addService(window.googletag.pubads())
          definedSlots.add(slotId)
        } catch (error) {
          markAdContainerFailed(slotId, `Define error: ${error.message}`)
        }
      })
      return true
    } catch (error) {
      markAdContainerFailed(slotId, `Define error: ${error.message}`)
      return false
    }
  }

  /**
   * Display ad in container
   */
    /**
   * Display ad in container - with dimension validation
   */
  const displayAd = (slotId) => {
    if (!window.googletag || !window.googletag.cmd) {
      markAdContainerFailed(slotId, 'GPT not initialized')
      return false
    }

    const container = document.getElementById(slotId)
    if (!container) return false

    // Check if container has actual dimensions before displaying
    if (container.offsetWidth === 0 || container.offsetHeight === 0) {
      // Use ResizeObserver to wait for container to get dimensions
      const observer = new ResizeObserver((entries) => {
        for (const entry of entries) {
          if (entry.target.offsetWidth > 0) {
            observer.disconnect()
            // Now display the ad
            window.googletag.cmd.push(function() {
              try {
                window.googletag.display(slotId)
                const slot = window.googletag.pubads().getSlots().find(s => s.getSlotElementId() === slotId)
                if (slot) {
                  window.googletag.pubads().refresh([slot])
                }
              } catch (error) {
                markAdContainerFailed(slotId, `Display error: ${error.message}`)
              }
            })
          }
        }
      })
      
      observer.observe(container)
      return true
    }

    // Container has dimensions, proceed normally
    try {
      window.googletag.cmd.push(function() {
        try {
          window.googletag.display(slotId)
          // Since we are using Single Request Architecture (enableSingleRequest),
          // simply calling display() for dynamically added slots might not trigger a fetch
          // if the initial SRA request has already completed.
          // We need to explicitly refresh the slot to fetch the ad.
          const slot = window.googletag.pubads().getSlots().find(s => s.getSlotElementId() === slotId)
          if (slot) {
            window.googletag.pubads().refresh([slot])
          }
        } catch (error) {
          markAdContainerFailed(slotId, `Display error: ${error.message}`)
        }
      })
      return true
    } catch (error) {
      markAdContainerFailed(slotId, `Display error: ${error.message}`)
      return false
    }
  }

  /**
   * Load top banner ad
   */
  const loadTopBannerAd = async (id, isFirstReview = false) => {
    await nextTick()

    const slotId = googleAdsConfig.containerIds.topBanner(id)
    const container = document.getElementById(slotId)

    if (!container) {
      // console.warn('[Google Ads] Top banner container not found:', slotId)
      return false
    }

    if (gptLoadFailed.value || gptLoadTimeout.value) {
      markAdContainerFailed(slotId, 'GPT unavailable')
      return false
    }

    try {
      const adUnitName = isFirstReview
        ? googleAdsConfig.adUnits.firstReview.top
        : googleAdsConfig.adUnits.secondReview.top

      const adUnitPath = buildAdUnitPath(adUnitName)
      const mapping = createSizeMapping('topBanner')
      const sizes = googleAdsConfig.adSizes.topBanner

      const defined = defineAdSlot(slotId, adUnitPath, sizes, mapping)
      if (defined) {
        displayAd(slotId)
        container.setAttribute('data-ad-status', 'requested')
        return true
      }
      return false
    } catch (error) {
      markAdContainerFailed(slotId, `Load error: ${error.message}`)
      return false
    }
  }

  /**
   * Load content ads for text reviews (middle and bottom)
   */
  const loadTextReviewAds = async (id, isFirstReview, adsLayout) => {
    await nextTick()

    const adUnitBase = isFirstReview
      ? googleAdsConfig.adUnits.firstReview
      : googleAdsConfig.adUnits.secondReview

    const results = {
      middleOne: false,
      bottom: false
    }

    try {
      // Middle banner (conditional on layout)
      const showMiddleOne = isFirstReview || adsLayout === googleAdsConfig.layouts.LAYOUT_1A

      if (showMiddleOne) {
        const middleSlotId = googleAdsConfig.containerIds.middleOne(id)
        const middleContainer = document.getElementById(middleSlotId)

        if (middleContainer) {
          const adUnitPath = buildAdUnitPath(adUnitBase.contentInner)
          const mapping = createSizeMapping('contentInner')
          const sizes = googleAdsConfig.adSizes.contentInner

          if (defineAdSlot(middleSlotId, adUnitPath, sizes, mapping)) {
            displayAd(middleSlotId)
            results.middleOne = true
          }
        }
      }

      // Bottom banner (always shown)
      const bottomSlotId = googleAdsConfig.containerIds.bottom(id)
      const bottomContainer = document.getElementById(bottomSlotId)

      if (bottomContainer) {
        const adUnitPath = buildAdUnitPath(adUnitBase.underContent)
        const mapping = createSizeMapping('underContent')
        const sizes = googleAdsConfig.adSizes.underContent

        if (defineAdSlot(bottomSlotId, adUnitPath, sizes, mapping)) {
          displayAd(bottomSlotId)
          results.bottom = true
        }
      }

      return results
    } catch (error) {
      // console.error('[Google Ads] Error loading text review ads:', error)
      return results
    }
  }

  /**
   * Load content ads for image reviews
   */
  const loadImageReviewAds = async (id, isFirstReview) => {
    await nextTick()

    const adUnitBase = isFirstReview
      ? googleAdsConfig.adUnits.firstReview
      : googleAdsConfig.adUnits.secondReview

    const results = {
      images: [],
      bottom: false
    }

    try {
      // Ads between images (0-4)
      for (let i = 0; i < 5; i++) {
        const imageSlotId = `${googleAdsConfig.containerIds.imagesPrefix}${i}-${id}`
        const container = document.getElementById(imageSlotId)

        if (container) {
          const adUnitPath = buildAdUnitPath(adUnitBase.contentInner)
          const mapping = createSizeMapping('contentInner')
          const sizes = googleAdsConfig.adSizes.contentInner

          if (defineAdSlot(imageSlotId, adUnitPath, sizes, mapping)) {
            displayAd(imageSlotId)
            container.classList.add('images-review-innerbanner-filled')
            results.images.push(i)
          }
        }
      }

      // Bottom banner for image reviews
      const bottomSlotId = googleAdsConfig.containerIds.imagesBottom(id)
      const bottomContainer = document.getElementById(bottomSlotId)

      if (bottomContainer) {
        const adUnitPath = buildAdUnitPath(adUnitBase.underContent)
        const mapping = createSizeMapping('underContent')
        const sizes = googleAdsConfig.adSizes.underContent

        if (defineAdSlot(bottomSlotId, adUnitPath, sizes, mapping)) {
          displayAd(bottomSlotId)
          results.bottom = true
        }
      }

      return results
    } catch (error) {
      // console.error('[Google Ads] Error loading image review ads:', error)
      return results
    }
  }

  /**
   * Load comment banner ad
   */
  const loadCommentBannerAd = async (commentRelation) => {
    await nextTick()

    const slotId = googleAdsConfig.containerIds.commentBanner(commentRelation)
    const container = document.getElementById(slotId)

    if (!container) {
      // console.warn('[Google Ads] Comment banner container not found:', slotId)
      return false
    }

    if (gptLoadFailed.value || gptLoadTimeout.value) {
      markAdContainerFailed(slotId, 'GPT unavailable')
      return false
    }

    try {
      const adUnitPath = buildAdUnitPath(googleAdsConfig.adUnits.firstReview.comments)
      const mapping = createSizeMapping('comments')
      const sizes = googleAdsConfig.adSizes.comments

      const defined = defineAdSlot(slotId, adUnitPath, sizes, mapping)
      if (defined) {
        displayAd(slotId)
        return true
      }
      return false
    } catch (error) {
      markAdContainerFailed(slotId, `Load error: ${error.message}`)
      return false
    }
  }

  /**
   * Load ads for first review
   */
  const loadFirstReviewAds = async (reviewData) => {
    if (!reviewData || !reviewData.main || !reviewData.main[0]) return

    const id = reviewData.main[0].ID
    const submType = reviewData.main[0].submType
    const reviewType = submType == 0 ? googleAdsConfig.reviewTypes.TEXT : googleAdsConfig.reviewTypes.IMAGES

    // Initialize GPT if needed
    if (!gptInitialized.value) {
      initializeGPT()
      await new Promise(resolve => setTimeout(resolve, 200))

      if (!gptInitialized.value) {
        // console.warn('[Google Ads] GPT not ready, skipping first review ads')
        return
      }
    }

    try {
      // Load top banner
      await loadTopBannerAd(id, true)

      // Load content ads based on review type
      if (reviewType === googleAdsConfig.reviewTypes.TEXT) {
        await loadTextReviewAds(id, true, reviewData.main[0].ads_layout)
      } else {
        await loadImageReviewAds(id, true)
      }
    } catch (error) {
      // console.error('[Google Ads] Error loading first review ads:', error)
    }
  }

  /**
   * Load ads for second or subsequent reviews
   */
  const loadSubsequentReviewAds = async (reviewData, adsLayout) => {
    if (!reviewData || !reviewData.main || !reviewData.main[0]) return

    const id = reviewData.main[0].ID
    const submType = reviewData.main[0].submType
    const reviewType = submType == 0 ? googleAdsConfig.reviewTypes.TEXT : googleAdsConfig.reviewTypes.IMAGES

    // Check if GPT failed - show fallback banners instead
    if (gptLoadFailed.value || gptLoadTimeout.value) {
      // console.warn('[Google Ads] GPT unavailable, showing fallback banners for review', id)
      // Show fallback for top banner
      const topBannerContainer = document.getElementById(googleAdsConfig.containerIds.topBanner(id))
      if (topBannerContainer) {
        showFallbackBanner(googleAdsConfig.containerIds.topBanner(id), topBannerContainer)
      }
      // Show fallback for bottom banner
      const bottomContainer = document.getElementById(googleAdsConfig.containerIds.bottom(id))
      if (bottomContainer) {
        showFallbackBanner(googleAdsConfig.containerIds.bottom(id), bottomContainer)
      }
      return
    }

    try {
      // Load top banner
      await loadTopBannerAd(id, false)

      // Load content ads based on review type and layout
      if (reviewType === googleAdsConfig.reviewTypes.TEXT) {
        // Determine if we should show middle banner based on layout
        const showMiddleOne = adsLayout === googleAdsConfig.layouts.LAYOUT_1A

        if (showMiddleOne) {
          await loadTextReviewAds(id, false, adsLayout)
        } else {
          // Layout 2: no middle ad, only bottom
          await loadTextReviewAds(id, false, googleAdsConfig.layouts.LAYOUT_2)
        }
      } else {
        await loadImageReviewAds(id, false)
      }
    } catch (error) {
      // console.error('[Google Ads] Error loading subsequent review ads:', error)
      // Show fallback banners instead of marking as failed
      const topBannerContainer = document.getElementById(googleAdsConfig.containerIds.topBanner(id))
      if (topBannerContainer) {
        showFallbackBanner(googleAdsConfig.containerIds.topBanner(id), topBannerContainer)
      }
      const bottomContainer = document.getElementById(googleAdsConfig.containerIds.bottom(id))
      if (bottomContainer) {
        showFallbackBanner(googleAdsConfig.containerIds.bottom(id), bottomContainer)
      }
    }
  }

  /**
   * Load ads for a review (main entry point)
   */
  const loadAdsForReview = async (reviewData, index, totalReviews) => {
    if (noAdsPage || !reviewData || !reviewData.main || !reviewData.main[0]) return

    const id = reviewData.main[0].ID
    const isFirstReview = index === 0
    const adsLayout = reviewData.main[0].ads_layout

    // Skip if review has ads disabled
    if (reviewData.main[0].no_cost == 1 || reviewData.main[0].IDnum) return

    // Check if GPT failed to load
    if (gptLoadFailed.value || gptLoadTimeout.value) {
      // console.warn('[Google Ads] Skipping ads due to GPT failure')
      return
    }

    // Initialize GPT if not already done
    if (!gptInitialized.value) {
      initializeGPT()
      await new Promise(resolve => setTimeout(resolve, 200))

      if (!gptInitialized.value) {
        // console.warn('[Google Ads] GPT not ready, skipping ads for this review')
        return
      }
    }

    try {
      if (isFirstReview) {
        await loadFirstReviewAds(reviewData)
      } else {
        await loadSubsequentReviewAds(reviewData, adsLayout)
      }
    } catch (error) {
      // console.error('[Google Ads] Error loading ads for review', id, ':', error)
    }
  }

  /**
   * Load ads for comments when they enter viewport
   */
  const loadCommentAdsOnScroll = (commentRelations) => {
    if (!commentRelations || commentRelations.length === 0) return

    // Prevent loading ads before GPT is initialized to avoid race condition with destroySlots()
    if (!gptInitialized.value) return

    if (gptLoadFailed.value || gptLoadTimeout.value || adBlockerDetected.value) {
      return
    }

    for (let commentRelation of commentRelations) {
      const commentCluster = document.getElementById(`commentsClusterBlck-${commentRelation}`)
      if (commentCluster && !commentCluster.getAttribute('data-ads-loaded')) {
        const rect = commentCluster.getBoundingClientRect()
        if (rect.top < window.innerHeight && rect.bottom > 0) {
          loadCommentBannerAd(commentRelation)
          commentCluster.setAttribute('data-ads-loaded', 'true')
        }
      }
    }
  }

  // Return public API
  return {
    // State
    gptInitialized,
    gptLoadFailed,
    gptLoadTimeout,
    adBlockerDetected,
    adLoadErrors,

    // Methods
    detectAdBlocker,
    initializeGPT,
    loadAdsForReview,
    loadCommentAdsOnScroll,
    showAllFallbackBanners,

    // Config access
    googleAdsConfig
  }
}
